import { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { MsalProvider } from "@azure/msal-react";
import { setApiTokenProvider } from "./api";
import App from "./App";
import { DebriefScreen } from "./debrief/DebriefScreen";
import FloorWalkerRoute from "./FloorWalkerRoute";
import { AuthGate } from "./auth/AuthGate";
import { consumeRedirectResult, getMsalInstance, resetMsalInstance, waitForNativeBridge } from "./auth/msal";
import { wireNativeMsalRedirect } from "./auth/native";
import { isNativeShell } from "./auth/platform";
import { acquireIdToken } from "./auth/token";
import { captureInstallPrompt, maybeShowLaunchChoice } from "./pwa/launchChoice";
import { registerServiceWorker } from "./pwa/registerServiceWorker";
import { isDebriefPath, isKioskBoot } from "./workspacePath";
import "./index.css";

const root = document.getElementById("root");
if (!root) {
  throw new Error("Root element #root not found");
}
const mount = root;
captureInstallPrompt();
registerServiceWorker();

function renderKiosk(): void {
  createRoot(mount).render(<FloorWalkerRoute />);
}

function SignedInApp() {
  const [path, setPath] = useState(() => window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  if (isDebriefPath(path)) return <DebriefScreen />;
  return <App />;
}

function renderApp(pca: ReturnType<typeof getMsalInstance>): void {
  createRoot(mount).render(
    <MsalProvider instance={pca}>
      <AuthGate>
        <SignedInApp />
      </AuthGate>
    </MsalProvider>,
  );
}

async function boot(): Promise<void> {
  if (isKioskBoot()) {
    renderKiosk();
    return;
  }
  if (!isNativeShell()) {
    await maybeShowLaunchChoice();
  }
  await waitForNativeBridge();
  resetMsalInstance();
  const pca = getMsalInstance();
  await pca.initialize();
  if (isNativeShell()) {
    await wireNativeMsalRedirect(pca);
  } else {
    const redirect = await consumeRedirectResult(pca);
    if (redirect?.idToken && redirect.account) {
      pca.setActiveAccount(redirect.account);
    } else {
      const account = pca.getAllAccounts()[0];
      if (account) {
        pca.setActiveAccount(account);
      }
    }
  }
  setApiTokenProvider(() => acquireIdToken(pca));
  renderApp(pca);
}

void boot().catch((error: unknown) => {
  console.error(error);
  const pca = getMsalInstance();
  setApiTokenProvider(() => acquireIdToken(pca));
  renderApp(pca);
});
